import "./Home.module.css";
import React from "react";
import { CalendarDays, Clock, Users, ExternalLink, Mic } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
import EnTeteDeNavigation from "@/reusable_sections/Header";
import Hero from "./sections/Hero";
import WhatIDoForYou from "./sections/WhatIDoForYou";
import HowItWorks from "./sections/HowItWorks";
import FeatureHighlight from "./sections/FeatureHighlight";
import AutomationDeepDive from "./sections/AutomationDeepDive";
import WhyWorkWithUs from "./sections/WhyWorkWithUs";
import Testimonials from "./sections/Testimonials";
import Services from "./sections/Services";
import CtaFinal from "./sections/CtaFinal";
import ReadyToTransformProject from "../PrivacyPolicy/sections/ReadyToTransformProject";
import Footer from "@/reusable_sections/Footer";

/* ───────────────────────────────────────────────
   Section Webinaires
   ─────────────────────────────────────────────── */

const webinars = [{
  id: 1,
  number: "1/5",
  title: "France Compétences a refusé l'enregistrement : quelles sont vos marges de manœuvre ?",
  date: "10 avril 2026",
  time: "11h00",
  duration: "1 heure",
  guest: "Élodie ARNAUD-CAVIGIOLI",
  status: "replay" as const,
  livestormUrl: "https://effet-conseil.com/wp-content/uploads/2026/04/20260410_Webinaire-1_Refus-et-marges-de-manoeuvre.pdf"
}, {
  id: 2,
  number: "2/5",
  title: "Votre certification est enregistrée : quelles obligations immédiates et quels risques à ne (surtout) pas sous-estimer ?",
  date: "12 juin 2026",
  time: "11h00",
  duration: "1 heure",
  guest: "Katarzyna FOSSATI",
  status: "replay" as const,
  livestormUrl: "https://effet-conseil.com/wp-content/uploads/2026/06/20260612_Webinaire_2_Obligations-immediates-et-risques.pdf"
}, {
  id: 3,
  number: "3/5",
  title: "Partenariats, habilitations, sous-traitance : votre modèle est-il juridiquement sécurisé ?",
  date: "Septembre 2026",
  time: "",
  duration: "1 heure",
  guest: "Invité(e) à confirmer",
  status: "announced" as const,
  livestormUrl: ""
}, {
  id: 4,
  number: "4/5",
  title: "Contrôle France Compétences : comment vous organiser et sécuriser votre réponse ?",
  date: "Octobre 2026",
  time: "",
  duration: "1 heure",
  guest: "Invité(e) à confirmer",
  status: "announced" as const,
  livestormUrl: ""
}, {
  id: 5,
  number: "5/5",
  title: "Renouvellement de votre certification : à quel moment anticiper pour éviter les difficultés ?",
  date: "Décembre 2026",
  time: "",
  duration: "1 heure",
  guest: "Invité(e) à confirmer",
  status: "announced" as const,
  livestormUrl: ""
}];
const statusConfig = {
  live: {
    label: "En direct",
    className: "bg-red-500 text-white animate-pulse"
  },
  replay: {
    label: "Présentation disponible",
    className: "bg-[#62751D] text-white"
  },
  upcoming: {
    label: "Inscription ouverte",
    className: "bg-[hsl(343,80%,35%)]/10 text-[hsl(343,80%,35%)]"
  },
  announced: {
    label: "Bientôt",
    className: "bg-slate-100 text-slate-600"
  }
};
function WebinarsSection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const featured = webinars.find(w => w.status === "live") || webinars.find(w => w.status === "upcoming" && w.livestormUrl) || [...webinars].reverse().find(w => w.status === "replay") || webinars[0];
  const others = webinars.filter(w => w.id !== featured.id);
  return <section id="webinaires" className="w-full bg-[#F5EDE8] py-14 md:py-20">
      <div ref={ref} className={cn("container mx-auto px-4 md:px-6 transition-all duration-800 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
        <div className="flex flex-col items-center text-center mb-10 md:mb-14 max-w-3xl mx-auto">
          <span className="inline-block font-bold uppercase tracking-widest text-[#62751D] mb-3 !text-sm">MINI-SÉRIE 2026 · 5 ÉPISODES</span>
          <h2 className="font-serif mb-4 !font-normal !text-[hsl(343,80%,35%)] text-3xl md:!text-[42px] leading-tight">
            Et maintenant, je fais quoi&nbsp;?
          </h2>
          <p className="text-base md:text-lg text-muted-foreground font-sans max-w-2xl mb-6">
            Les webinaires stratégiques de la certification — animés par{" "}
            <strong>Christelle CHARRIER-ROBELIN</strong> avec un invité
            expert différent à chaque épisode. Des apports concrets suivis
            d'échanges à partir de vos situations réelles, en toute
            confidentialité.
          </p>
          <div className="inline-flex flex-wrap items-center justify-center gap-4 text-sm text-slate-600 bg-white/60 border border-slate-200 rounded-sm px-6 py-3">
            <span className="flex items-center gap-1.5">
              <Clock className="size-4 text-[#62751D]" />
              1 heure
            </span>
            <span className="text-slate-300">|</span>
            <span className="flex items-center gap-1.5">
              <Mic className="size-4 text-[#62751D]" />
              35-40 min d'apports + 20-25 min de Q&R
            </span>
            <span className="text-slate-300">|</span>
            <span className="flex items-center gap-1.5">
              <Users className="size-4 text-[#62751D]" />
              Porteurs de projets RNCP / RS
            </span>
          </div>
        </div>

        <div className="max-w-4xl mx-auto mb-10">
          <div className="bg-white rounded-sm shadow-xl border border-border/50 overflow-hidden">
            <div className="bg-[hsl(343,80%,35%)] px-6 py-3 flex items-center justify-between">
              <span className="text-white font-serif font-semibold text-sm">
                Webinaire {featured.number}
              </span>
              <span className={cn("text-xs font-bold px-3 py-1 rounded-full", statusConfig[featured.status].className)}>
                {statusConfig[featured.status].label}
              </span>
            </div>
            <div className="p-6 md:p-8">
              <h3 className="font-serif text-xl md:text-2xl text-slate-900 font-bold mb-4 leading-snug">
                {featured.title}
              </h3>
              <div className="flex flex-wrap gap-4 text-sm text-slate-600 mb-4">
                <span className="flex items-center gap-1.5">
                  <CalendarDays className="size-4 text-[#62751D]" />
                  {featured.date}
                </span>
                {featured.time && <span className="flex items-center gap-1.5">
                    <Clock className="size-4 text-[#62751D]" />
                    {featured.time} — {featured.duration}
                  </span>}
              </div>
              <p className="text-sm text-slate-500 mb-6 flex items-center gap-1.5">
                <Mic className="size-4 text-[hsl(343,80%,35%)]" />
                Christelle CHARRIER-ROBELIN &{" "}
                {featured.guest}
              </p>
              {featured.livestormUrl && <a href={featured.livestormUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center bg-[hsl(343,80%,35%)] hover:bg-[hsl(343,80%,30%)] text-white rounded-sm px-8 h-12 text-base font-serif transition-colors">
                  {featured.status === "replay" ? "Consulter la présentation" : featured.status === "live" ? "Rejoindre le direct" : "S'inscrire"}
                  <ExternalLink className="size-4 ml-2" />
                </a>}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto mb-10">
          {others.map(w => <div key={w.id} className="bg-white rounded-sm border border-border/50 shadow-sm p-5 flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold text-[hsl(343,80%,35%)] font-serif">
                  {w.number}
                </span>
                <span className={cn("text-[10px] font-bold px-2 py-0.5 rounded-full", statusConfig[w.status].className)}>
                  {statusConfig[w.status].label}
                </span>
              </div>
              <h4 className="font-serif text-sm font-semibold text-slate-800 mb-2 leading-snug flex-grow">
                {w.title}
              </h4>
              <p className="text-xs text-slate-500 mb-1">
                <CalendarDays className="size-3 inline mr-1" />
                {w.date}
                {w.time && ` · ${w.time}`}
              </p>
              <p className="text-xs text-slate-400 mb-3">
                <Mic className="size-3 inline mr-1" />
                {w.guest}
              </p>
              {w.livestormUrl ? <a href={w.livestormUrl} target="_blank" rel="noopener noreferrer" className="mt-auto text-xs font-semibold text-[hsl(343,80%,35%)] hover:underline inline-flex items-center gap-1">
                  {w.status === "replay" ? "Consulter la présentation" : "S'inscrire"}
                  <ExternalLink className="size-3" />
                </a> : <span className="mt-auto text-xs text-slate-400 italic">
                  {w.status === "upcoming" ? "Lien d'inscription à venir" : "Date et inscription à venir"}
                </span>}
            </div>)}
        </div>

        <div className="text-center">
          <p className="text-sm text-slate-600 mb-4 font-sans">
            Vous avez des questions suite à un webinaire ?
          </p>
          <Button asChild variant="outline" className="border-[hsl(343,80%,35%)] text-[hsl(343,80%,35%)] hover:bg-[hsl(343,80%,35%)]/5 rounded-sm px-8 h-12 font-serif">
            <Link to="/ContactPage">Échangeons sur votre projet</Link>
          </Button>
        </div>
      </div>
    </section>;
}

/* ───────────────────────────────────────────────
   Page d'accueil
   ─────────────────────────────────────────────── */

export default function HomePage() {
  return <div>
      <EnTeteDeNavigation />
      <Hero />
      <WhatIDoForYou />
      <HowItWorks />
      <FeatureHighlight />
      <AutomationDeepDive />
      <WhyWorkWithUs />
      <Testimonials />
      <WebinarsSection />
      <CtaFinal />
      <Services />
      <ReadyToTransformProject />
      <Footer />
    </div>;
}